import React, { useEffect, useRef } from 'react'

import { useInView } from 'react-cool-inview'

import { Spinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, bool, array, func, number } from 'prop-types'

import useSentry from 'Pods/Shared/Hooks/useSentry'

import { NOOP } from 'Utils/stableDefaults'

import CartItem from './Components/CartItem/CartItem'

function CartItemsList({
  items,
  included,
  onLoadNextPage,
  hasError = false,
  isLastPageLoaded,
  trackCartRemoveGA4 = NOOP,
  trackAddToCart = NOOP,
  isCartBelowThreshold,
  isCartAboveThreshold,
  shouldShowCartProgress,
  actionType = 'remove',
  listType = '',
  useIncludedData = true,
  saleThreshold = null,
  locale = 'en',
}) {
  const containerRef = useRef(null)
  const timeoutRef = useRef(null)
  const hasReportedRef = useRef(false)
  const { captureException } = useSentry()

  const spinnerVisible = !isLastPageLoaded && !hasError

  const { observe } = useInView({
    root: containerRef?.current,
    onEnter: () => {
      try {
        const result = onLoadNextPage()
        if (result && typeof result.catch === 'function') {
          result.catch((error) => {
            captureException(error, {
              tags: { section: 'Cart', component: 'CartItemsList', event: 'onLoadNextPage' },
              extra: {
                msg: 'onLoadNextPage promise rejected',
                itemsCount: items?.length,
                includedCount: included?.length,
                isLastPageLoaded,
                hasError,
                listType,
                actionType,
                useIncludedData,
                saleThreshold,
                url: typeof window !== 'undefined' ? window.location.href : undefined,
              },
            })
          })
        }
        return result
      } catch (error) {
        captureException(error, {
          tags: { section: 'Cart', component: 'CartItemsList', event: 'onLoadNextPage' },
          extra: {
            msg: 'onLoadNextPage threw synchronously',
            itemsCount: items?.length,
            includedCount: included?.length,
            isLastPageLoaded,
            hasError,
            listType,
            actionType,
            useIncludedData,
            saleThreshold,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          },
        })
        return null
      }
    },
  })

  useEffect(() => {
    // Report if spinner is visible for an unexpectedly long time
    if (spinnerVisible) {
      // reset reported flag for a new spinner session
      hasReportedRef.current = false
      timeoutRef.current = setTimeout(() => {
        if (!hasReportedRef.current) {
          hasReportedRef.current = true
          captureException(new Error('CartItemsList spinner visible too long'), {
            tags: { section: 'Cart', component: 'CartItemsList', event: 'spinner-timeout' },
            extra: {
              msg: 'Spinner remained visible beyond threshold',
              thresholdMs: 15000,
              itemsCount: items?.length,
              includedCount: included?.length,
              isLastPageLoaded,
              hasError,
              listType,
              actionType,
              useIncludedData,
              saleThreshold,
              url: typeof window !== 'undefined' ? window.location.href : undefined,
            },
          })
        }
      }, 15000)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [spinnerVisible])

  return (
    <div className="popover-content__items">
      <div className="product-list product-list--new">
        {items.map((cartItem, index) => (
          <CartItem
            key={cartItem.id}
            data={cartItem}
            included={included}
            trackCartRemoveGA4={trackCartRemoveGA4}
            trackAddToCart={trackAddToCart}
            isCartBelowThreshold={isCartBelowThreshold}
            isCartAboveThreshold={isCartAboveThreshold}
            shouldShowCartProgress={shouldShowCartProgress}
            itemIndex={index}
            listType={listType}
            actionType={actionType}
            useIncludedData={useIncludedData}
            saleThreshold={saleThreshold}
            locale={locale}
          />
        ))}
        {spinnerVisible && (
          <div ref={observe} className="row justify-content-center cgt-mr-10 cgt-ml-10 cgt-mb-10">
            <Spinner size="small" />
          </div>
        )}
      </div>
    </div>
  )
}

CartItemsList.propTypes = {
  items: array.isRequired,
  included: array.isRequired,
  onLoadNextPage: func.isRequired,
  hasError: bool,
  isLastPageLoaded: bool.isRequired,
  trackCartRemoveGA4: func,
  trackAddToCart: func,
  isCartBelowThreshold: bool.isRequired,
  isCartAboveThreshold: bool.isRequired,
  shouldShowCartProgress: bool.isRequired,
  actionType: string,
  listType: string,
  useIncludedData: bool,
  saleThreshold: number,
  locale: string,
}

export default CartItemsList
